import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import $api from 'http'
import Notification from 'utils/Notifications'

import { Spinner, EmptyState } from 'components'
import SyllabusHeader from './components/SyllabusHeader/SyllabusHeader'
import SemesterCard from './components/SemesterCard/SemesterCard'
import styles from './SyllabusViewPage.module.scss'

const SyllabusViewPage = () => {
  const { t } = useTranslation('common')
  const [me, setMe] = useState(null)
  const [profile, setProfile] = useState(null)
  const [syllabuses, setSyllabuses] = useState([])
  const [activeSyllabus, setActiveSyllabus] = useState(null)
  const [tree, setTree] = useState(null)
  const [progress, setProgress] = useState([])
  const [assignments, setAssignments] = useState([])
  const [openSemesters, setOpenSemesters] = useState({})
  const [loading, setLoading] = useState(true)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const studentId = profile?.id

  const loadMe = async () => {
    try {
      const [m, s] = await Promise.allSettled([
        $api.get('/admin-panel/me/'),
        $api.get('/students/me/'),
      ])
      setMe(m.status === 'fulfilled' ? m.value.data : null)
      setProfile(s.status === 'fulfilled' ? s.value.data : null)
    } catch {

    }
  }

  const loadSyllabuses = async () => {
    try {
      const r = await $api.get('/syllabuses/')
      const list = r.data?.results || r.data || []
      setSyllabuses(list)
      if (list.length && !activeSyllabus) setActiveSyllabus(list[0].id)
    } catch {
      setSyllabuses([])
    }
  }

  const loadTree = async (sylId) => {
    if (!sylId) return
    try {
      const r = await $api.get('/syllabus-nested/', { params: { syllabus: sylId } })
      const data = r.data?.results || r.data || []
      const root = Array.isArray(data) ? data.find((x) => x.id === sylId) || data[0] : data
      setTree(root)
    } catch {
      try {
        const r2 = await $api.get(`/syllabus/${sylId}/`)
        setTree(r2.data)
      } catch {
        setTree(null)
      }
    }
  }

  const loadProgress = async () => {
    if (!studentId) return
    try {
      const r = await $api.get('/academic/progress/', { params: { student: studentId } })
      setProgress(r.data?.results || r.data || [])
    } catch {
      setProgress([])
    }
  }

  const loadAssignments = async () => {
    try {
      const r = await $api.get('/assignments/assignments/', { params: { only_published: 1 } })
      setAssignments(r.data?.results || r.data || [])
    } catch {
      setAssignments([])
    }
  }

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      await loadMe()
      await loadSyllabuses()
      await loadAssignments()
      setLoading(false)
    })()
  }, [])

  useEffect(() => {
    loadProgress()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId])

  useEffect(() => {
    if (activeSyllabus) loadTree(activeSyllabus)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSyllabus])

  const progressByTheme = useMemo(() => {
    const m = {}
    progress.forEach((p) => {
      const tId = p.theme?.id || p.theme
      if (tId != null) m[tId] = p
    })
    return m
  }, [progress])

  const assignmentsByTheme = useMemo(() => {
    const m = {}
    assignments.forEach((a) => {
      const tId = a.theme?.id || a.theme
      if (tId != null) {
        if (!m[tId]) m[tId] = []
        m[tId].push(a)
      }
    })
    return m
  }, [assignments])

  const semesters = useMemo(() => {
    if (!tree) return []
    return tree.semesters || tree.semester_set || []
  }, [tree])

  const setStatus = async (theme, status) => {
    try {
      const existing = progressByTheme[theme.id]
      if (existing) {
        await $api.patch(`/academic/progress/${existing.id}/`, { status })
      } else {
        await $api.post('/academic/progress/', { theme: theme.id, status })
      }
      setNotify({ isOpen: true, message: t('syllabus.status_updated'), type: 'success', sound: 'success' })
      loadProgress()
    } catch {
      setNotify({ isOpen: true, message: t('syllabus.save_failed'), type: 'error', sound: 'error' })
    }
  }

  const semesterStats = (sem) => {
    const themes = (sem.subjects || []).flatMap((s) => s.themes || [])
    const total = themes.length
    const done = themes.filter((t) => progressByTheme[t.id]?.status === 'done').length
    return { total, done, percent: total ? Math.round((done / total) * 100) : 0 }
  }

  if (loading) {
    return (
      <div className={styles.loaderWrap}>
        <Spinner.Center />
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <SyllabusHeader
        profile={profile}
        me={me}
        syllabuses={syllabuses}
        activeSyllabus={activeSyllabus}
        setActiveSyllabus={setActiveSyllabus}
      />

      {!tree ? (
        <EmptyState iconName="syllabus" title={t('syllabus.select_plan_hint')} />
      ) : (
        <div className={styles.content}>
          {semesters.length === 0 && (
            <EmptyState iconName="schedule" title={t('syllabus.no_semesters')} />
          )}
          {semesters.map((sem) => (
            <SemesterCard
              key={sem.id}
              sem={sem}
              open={!!openSemesters[sem.id]}
              toggleOpen={() =>
                setOpenSemesters((p) => ({ ...p, [sem.id]: !p[sem.id] }))
              }
              stats={semesterStats(sem)}
              progressByTheme={progressByTheme}
              assignmentsByTheme={assignmentsByTheme}
              setStatus={setStatus}
            />
          ))}
        </div>
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default SyllabusViewPage
